// Standalone legal pages (Privacy Policy + Terms of Service).
// Rendered by App.jsx based on window.location.pathname (/privacy, /terms) so
// they work on direct load without logging in. nginx serves index.html for any
// path (SPA fallback), so deep links resolve here.
import React from 'react'

const C = {
  bg:   '#080C12',
  text: '#F1F5F9',
  head: '#00D97E',
  link: '#3B82F6',
  muted:'#64748B',
}

// Linkify emails and bare URLs in body text, styled blue.
function renderText(s) {
  const parts = s.split(/(\S+@\S+\.\S+|(?:https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(?:\/[^\s]*)?)/gi)
  return parts.map((p, i) => {
    if (/^\S+@\S+\.\S+$/.test(p)) {
      return <a key={i} href={`mailto:${p}`} style={{ color: C.link, textDecoration: 'none' }}>{p}</a>
    }
    if (/^(https?:\/\/)?[a-z0-9.-]+\.[a-z]{2,}(\/[^\s]*)?$/i.test(p) && /\.[a-z]{2,}/i.test(p) && !/^\d+\.\d+$/.test(p)) {
      const href = p.startsWith('http') ? p : `https://${p}`
      return <a key={i} href={href} target="_blank" rel="noopener noreferrer" style={{ color: C.link, textDecoration: 'none' }}>{p}</a>
    }
    return <React.Fragment key={i}>{p}</React.Fragment>
  })
}

function LegalDoc({ title, updated, sections }) {
  return (
    <div style={{ background: C.bg, minHeight: '100vh', color: C.text,
      fontFamily: "-apple-system,'SF Pro Display',Arial,sans-serif" }}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 20px 64px' }}>
        <a href="/" style={{ color: C.link, fontSize: 13, textDecoration: 'none' }}>← Back to LINEBRKR</a>
        <h1 style={{ color: C.head, fontSize: 26, fontWeight: 900, letterSpacing: '-0.02em', margin: '18px 0 4px' }}>{title}</h1>
        <div style={{ color: C.muted, fontSize: 13, marginBottom: 8 }}>{updated}</div>
        {sections.map((sec, i) => (
          <section key={i} style={{ marginTop: 22 }}>
            {sec.heading && (
              <h2 style={{ color: C.head, fontSize: 16, fontWeight: 800, margin: '0 0 8px' }}>{sec.heading}</h2>
            )}
            {sec.body.map((para, j) => (
              <p key={j} style={{ fontSize: 14, lineHeight: 1.6, color: C.text, margin: '0 0 10px', whiteSpace: 'pre-line' }}>
                {renderText(para)}
              </p>
            ))}
          </section>
        ))}
      </div>
    </div>
  )
}

const PRIVACY_SECTIONS = [
  { heading: '1. Who We Are', body: [
    'LINEBRKR is an MLB analytics and intelligence platform operated by LINEBRKR LLC. We provide pick analysis, probability modeling, and batting contact intelligence for informational purposes only.',
    'Contact: privacy@linebrkr.app',
  ]},
  { heading: '2. What Data We Collect', body: [
    'Account data: email address, collected at signup via Clerk. Used for authentication, account management, and transactional emails only.',
    'Subscription data: Stripe customer ID, subscription plan, subscription status. Stored in our database. Used to gate features based on your plan.',
    'Usage data: which features you use, which tabs you open, session length. Collected via PostHog analytics. Used to improve the product.',
    'Technical data: IP address, browser type, device type. Collected in server logs. Used for security, rate limiting, and abuse prevention. Retained for 30 days.',
    'Self-reported data: any P&L records or bet history you choose to enter in the Record tab. Stored in our database. Never shared with third parties.',
  ]},
  { heading: '3. What We Do Not Collect', body: [
    'We do not collect payment card details. All payment processing is handled by Stripe. We never see or store your card number, CVV, or billing address.',
    'We do not sell your data to third parties. Ever.',
    'We do not collect data from anyone under 21. If you are under 21 do not use this service.',
  ]},
  { heading: '4. How We Use Your Data', body: [
    'To provide the service: authenticate your account, determine your subscription plan, deliver picks and analysis.',
    'To improve the product: understand which features are used, identify bugs, improve pick quality.',
    'To communicate with you: subscription receipts, lineup alerts, product updates. You can opt out of product updates at any time.',
    'To prevent abuse: rate limiting, fraud detection, security monitoring.',
  ]},
  { heading: '5. Who We Share Data With', body: [
    'Clerk: handles authentication and user accounts. Privacy policy at clerk.com/legal/privacy.',
    'Stripe: handles payments and subscriptions. Privacy policy at stripe.com/privacy.',
    'PostHog: handles product analytics. Data stored in EU region. Privacy policy at posthog.com/privacy.',
    'Railway: hosts our servers and database. Privacy policy at railway.app/legal/privacy.',
    'We do not share your data with sportsbooks, advertisers, data brokers, or any other third parties.',
  ]},
  { heading: '6. Your Rights', body: [
    'You have the right to access all data we hold about you. Email privacy@linebrkr.app to request a full data export.',
    'You have the right to delete your account and all associated data. Go to Settings and tap Delete Account. We will process deletion within 72 hours. Note: Stripe retains payment records for 7 years as required by law.',
    'You have the right to correct inaccurate data. Email privacy@linebrkr.app.',
    'You have the right to object to analytics tracking. Email privacy@linebrkr.app and we will remove you from PostHog.',
    'If you are in the EU or UK you have additional rights under GDPR including the right to data portability and the right to lodge a complaint with your local supervisory authority.',
  ]},
  { heading: '7. Data Retention', body: [
    'Account data: retained until you delete your account.',
    'Subscription data: retained for 7 years after last transaction for tax and legal compliance.',
    'Usage analytics: retained for 12 months then deleted.',
    'Server logs: retained for 30 days then deleted automatically.',
    'Self-reported P&L data: deleted immediately on account deletion request.',
  ]},
  { heading: '8. Security', body: [
    'We implement industry-standard security measures including encrypted connections (HTTPS), JWT-based authentication, environment variable management for secrets, and rate limiting on all API endpoints.',
    'No card data ever touches our servers. All payments are processed directly by Stripe.',
    'In the event of a data breach we will notify affected users within 72 hours.',
  ]},
  { heading: '9. Cookies and Tracking', body: [
    'We use PostHog for product analytics. PostHog uses a first-party cookie to identify returning users. If you are in the EU we will ask for your consent before activating analytics.',
    'We do not use advertising cookies. We do not use third-party tracking pixels.',
  ]},
  { heading: '10. Changes to This Policy', body: [
    'We will notify you by email if we make material changes to this policy. The latest version is always available at linebrkr.app/privacy.',
  ]},
  { heading: '11. Contact', body: [
    'For any privacy questions or requests: privacy@linebrkr.app',
  ]},
]

const TERMS_SECTIONS = [
  { heading: '1. Acceptance of Terms', body: [
    'By creating an account or using LINEBRKR you agree to these Terms of Service. If you do not agree do not use the service.',
    'You must be 21 years of age or older to use LINEBRKR. By using the service you confirm you are 21 or older.',
  ]},
  { heading: '2. What LINEBRKR Is', body: [
    'LINEBRKR is an MLB analytics and intelligence platform. We provide statistical analysis, probability modeling, and pick intelligence for informational and entertainment purposes only.',
    'LINEBRKR is NOT a sportsbook. We do not accept bets. We do not handle wagers. We do not have any financial interest in the outcome of any game.',
  ]},
  { heading: '3. No Guarantee of Outcomes', body: [
    'THE PICKS, PROBABILITIES, AND ANALYSIS PROVIDED BY LINEBRKR ARE FOR INFORMATIONAL PURPOSES ONLY. PAST PERFORMANCE DOES NOT GUARANTEE FUTURE RESULTS.',
    'We make no representation that using LINEBRKR will result in profitable betting outcomes. Sports betting involves substantial risk. You may lose money.',
    'You are solely responsible for your own betting decisions. LINEBRKR accepts no responsibility for any financial losses incurred as a result of using this service.',
  ]},
  { heading: '4. Responsible Gambling', body: [
    'We strongly encourage responsible gambling. If you or someone you know has a gambling problem please seek help.',
    'National Council on Problem Gambling helpline: 1-800-522-4700',
    'BeGambleAware: begambleaware.org',
    'If you wish to exclude yourself from gambling please contact your sportsbook directly.',
  ]},
  { heading: '5. Subscription and Payment', body: [
    'Free tier: access to limited picks at no cost. No payment required.',
    'Pro tier: full access at $19.99 per month or $149 per year. Billed automatically until cancelled.',
    'Founding member tier: available to early access users only at $9.99 per month locked permanently. Non-transferable.',
    'All payments are processed by Stripe. By subscribing you authorize recurring charges to your payment method.',
    'You may cancel your subscription at any time through the app Settings or through the Stripe customer portal. Cancellation takes effect at the end of the current billing period. You retain access until the period ends.',
    'Refunds: we offer a full refund within 7 days of your first charge if you are not satisfied. Contact support@linebrkr.app. Subsequent charges are non-refundable.',
  ]},
  { heading: '6. Acceptable Use', body: [
    'You may use LINEBRKR for your own personal informational purposes only.',
    'You may not reproduce, redistribute, resell, or republish our picks, probabilities, or analysis without written permission.',
    'You may not use automated tools to scrape or bulk download our data.',
    'You may not share your account credentials with others.',
    'You may not use the service for any illegal purpose including in jurisdictions where sports betting is prohibited.',
  ]},
  { heading: '7. Intellectual Property', body: [
    'All content, analysis, formulas, algorithms, and software in LINEBRKR are the property of LINEBRKR LLC. All rights reserved.',
    'The LINEBRKR name and brand are proprietary. You may not use them without written permission.',
  ]},
  { heading: '8. Disclaimer of Warranties', body: [
    'LINEBRKR is provided as-is without warranties of any kind. We do not warrant that the service will be uninterrupted, error-free, or that picks will achieve any particular outcome.',
  ]},
  { heading: '9. Limitation of Liability', body: [
    'TO THE MAXIMUM EXTENT PERMITTED BY LAW, LINEBRKR LLC SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM USE OF THE SERVICE, INCLUDING BUT NOT LIMITED TO GAMBLING LOSSES.',
    'OUR TOTAL LIABILITY TO YOU FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID TO US IN THE 3 MONTHS PRECEDING THE CLAIM.',
  ]},
  { heading: '10. Termination', body: [
    'We reserve the right to suspend or terminate your account at any time if you violate these Terms of Service.',
    'You may delete your account at any time through Settings.',
  ]},
  { heading: '11. Changes to Terms', body: [
    'We will notify you by email of material changes to these terms. Continued use of the service after changes constitutes acceptance.',
  ]},
  { heading: '12. Governing Law', body: [
    'These terms are governed by the laws of the State of New Jersey, United States. Any disputes shall be resolved in the courts of New Jersey.',
  ]},
  { heading: '13. Contact', body: [
    'For any questions about these terms: support@linebrkr.app',
  ]},
]

export function PrivacyPolicy() {
  return <LegalDoc title="LINEBRKR Privacy Policy" updated="Last updated: June 2026" sections={PRIVACY_SECTIONS} />
}

export function TermsOfService() {
  return <LegalDoc title="LINEBRKR Terms of Service" updated="Last updated: June 2026" sections={TERMS_SECTIONS} />
}

const DISCLAIMER_SECTIONS = [
  { heading: '1. Research Only', body: [
    'LINEBRKR is a statistical research and analytics tool for MLB player props. Everything on the service, including projections, ratings, probabilities, and write-ups, is for informational and entertainment purposes only. It is not betting, financial, investment, or legal advice.',
    'We are not a sportsbook, casino, betting exchange, or licensed gaming operator. We do not accept, place, or settle bets, and we do not hold funds for wagering.'] },
  { heading: '2. No Guaranteed Outcomes', body: [
    'A projection or rating is a model estimate of how likely something is, not a promise. Baseball is high variance and any single pick can miss. We do not guarantee any result, and past performance does not predict future results.',
    'You are solely responsible for your own decisions. LINEBRKR accepts no responsibility for any losses incurred from using the service.'] },
  { heading: '3. 21+ and Responsible Play', body: [
    'You must be 21 years of age or older to use LINEBRKR. Only risk what you can afford to lose.',
    'If you or someone you know may have a gambling problem, help is available. Call or text 1-800-GAMBLER.'] },
  { heading: '4. Contact', body: ['Questions: support@linebrkr.app'] },
]

export function Disclaimer() {
  return <LegalDoc title="LINEBRKR Disclaimer" updated="Last updated: August 2026" sections={DISCLAIMER_SECTIONS} />
}
